import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import Swal from "sweetalert2";

import request from "../../../Helpers/API";
import { CategoriaMaquinariaArray } from "../../../Arrays/CategoriaMaquinariaArray";
import { TipoMaquinariaArray } from "../../../Arrays/TipoMaquinariaArray";
import { MarcaMaquinariaArray } from "../../../Arrays/MarcaMaquinariaArray";

const FORM_VACIO = {
  categoria: "",
  tipo: "",
  marca: "",
  modelo: "",
  serie: "",
  anio: "",
  comentarios: "",
};

// Regla de negocio: categoría "JOHN DEERE" implica siempre marca "JOHN DEERE"
const CATEGORIA_JOHN_DEERE = "JD";
const MARCA_JOHN_DEERE = "JOHN DEERE";

// Solo dígitos, máximo 4 (año)
const filtrarAnio = (value) => value.replace(/\D/g, "").slice(0, 4);

// Convierte una fila del listado (ParqueMaquinariaTabla) al formulario de este modal
const mapMaquinaToForm = (m) => ({
  categoria: m?.categoria || "",
  tipo: m?.tipo || "",
  marca: m?.marca || "",
  modelo: m?.modelo || "",
  serie: m?.serie || "",
  anio: m?.anio ? String(m.anio) : "",
  comentarios: m?.comentarios || "",
});

/**
 * ParqueMaquinariaModal
 * Alta/edición de una máquina del Parque de Maquinaria de un cliente. "maquina" viene completa
 * desde el listado ya cargado (ParqueMaquinariaTabla) — no se pide un detalle aparte al backend.
 * Tipo y Marca son catálogo + texto libre: el catálogo solo sugiere, el usuario puede escribir
 * y enviar cualquier valor sin que exista en el listado (excepto Marca cuando Categoría es
 * "JOHN DEERE" — ver marcaBloqueada).
 */
export const ParqueMaquinariaModal = ({ open, onClose, idcliente, maquina, onGuardado }) => {
  const [form, setForm] = useState(FORM_VACIO);

  const [mostrarTipos, setMostrarTipos] = useState(false);
  const [posicionTipo, setPosicionTipo] = useState(null);
  const buscadorTipoRef = useRef(null);
  const dropdownTipoRef = useRef(null);

  const [mostrarMarcas, setMostrarMarcas] = useState(false);
  const [posicionMarca, setPosicionMarca] = useState(null);
  const buscadorMarcaRef = useRef(null);
  const dropdownMarcaRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(maquina ? mapMaquinaToForm(maquina) : FORM_VACIO);
  }, [open, maquina]);

  const handleChange = (campo) => (e) => {
    setForm((prev) => ({ ...prev, [campo]: e.target.value }));
  };

  const handleChangeAnio = (e) => {
    setForm((prev) => ({ ...prev, anio: filtrarAnio(e.target.value) }));
  };

  const marcaSeleccionada = MarcaMaquinariaArray.find((m) => m.id === form.marca) || null;
  const marcaBloqueada = form.categoria === CATEGORIA_JOHN_DEERE;

  // Categoría "JOHN DEERE" fuerza la marca y no deja quitarla — solo elegir otra categoría la libera
  useEffect(() => {
    if (form.categoria !== CATEGORIA_JOHN_DEERE) return;
    setForm((prev) => (prev.marca === MARCA_JOHN_DEERE ? prev : { ...prev, marca: MARCA_JOHN_DEERE }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.categoria]);

  // El catálogo solo sugiere — se filtra sobre lo que el usuario ya escribió en el campo
  const resultadosTipo = mostrarTipos
    ? TipoMaquinariaArray.filter((t) => t.display.toLowerCase().includes(form.tipo.toLowerCase()))
    : [];
  const resultadosMarca = mostrarMarcas
    ? MarcaMaquinariaArray.filter((m) => m.display.toLowerCase().includes(form.marca.toLowerCase()))
    : [];

  // Dropdown de sugerencias en portal hacia document.body, posición calculada en JS — a
  // diferencia de un <select> nativo (cuya dirección hacia arriba/abajo decide el navegador
  // según espacio disponible), este siempre abre debajo del campo. Mismo patrón que el
  // buscador de Cultivo (CRM_ModalCultivo.js), reutilizado para Tipo y Marca
  const calcularPosicionTipo = () => {
    if (!buscadorTipoRef.current) return;
    const rect = buscadorTipoRef.current.getBoundingClientRect();
    setPosicionTipo({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  };

  const calcularPosicionMarca = () => {
    if (!buscadorMarcaRef.current) return;
    const rect = buscadorMarcaRef.current.getBoundingClientRect();
    setPosicionMarca({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  };

  useEffect(() => {
    if (!mostrarTipos) return;
    calcularPosicionTipo();

    const handleReposicionar = () => calcularPosicionTipo();
    const handleClickFuera = (e) => {
      const dentroDelBuscador = buscadorTipoRef.current && buscadorTipoRef.current.contains(e.target);
      const dentroDelDropdown = dropdownTipoRef.current && dropdownTipoRef.current.contains(e.target);
      if (!dentroDelBuscador && !dentroDelDropdown) setMostrarTipos(false);
    };

    window.addEventListener("scroll", handleReposicionar, true);
    window.addEventListener("resize", handleReposicionar);
    document.addEventListener("mousedown", handleClickFuera);
    return () => {
      window.removeEventListener("scroll", handleReposicionar, true);
      window.removeEventListener("resize", handleReposicionar);
      document.removeEventListener("mousedown", handleClickFuera);
    };
  }, [mostrarTipos]);

  useEffect(() => {
    if (!mostrarMarcas) return;
    calcularPosicionMarca();

    const handleReposicionar = () => calcularPosicionMarca();
    const handleClickFuera = (e) => {
      const dentroDelBuscador = buscadorMarcaRef.current && buscadorMarcaRef.current.contains(e.target);
      const dentroDelDropdown = dropdownMarcaRef.current && dropdownMarcaRef.current.contains(e.target);
      if (!dentroDelBuscador && !dentroDelDropdown) setMostrarMarcas(false);
    };

    window.addEventListener("scroll", handleReposicionar, true);
    window.addEventListener("resize", handleReposicionar);
    document.addEventListener("mousedown", handleClickFuera);
    return () => {
      window.removeEventListener("scroll", handleReposicionar, true);
      window.removeEventListener("resize", handleReposicionar);
      document.removeEventListener("mousedown", handleClickFuera);
    };
  }, [mostrarMarcas]);

  const handleSeleccionarTipo = (t) => {
    setForm((prev) => ({ ...prev, tipo: t.display }));
    setMostrarTipos(false);
  };

  const handleSeleccionarMarca = (m) => {
    setForm((prev) => ({ ...prev, marca: m.display }));
    setMostrarMarcas(false);
  };

  const validar = () => {
    if (!form.categoria) { Swal.fire("PARQUE DE MAQUINARIA", "La categoría es requerida", "warning"); return false; }
    if (!form.tipo.trim()) { Swal.fire("PARQUE DE MAQUINARIA", "El tipo es requerido", "warning"); return false; }
    if (!form.marca.trim()) { Swal.fire("PARQUE DE MAQUINARIA", "La marca es requerida", "warning"); return false; }
    if (!form.modelo.trim()) { Swal.fire("PARQUE DE MAQUINARIA", "El modelo es requerido", "warning"); return false; }
    if (!form.serie.trim()) { Swal.fire("PARQUE DE MAQUINARIA", "La serie es requerida", "warning"); return false; }
    if (form.anio.length !== 4) { Swal.fire("PARQUE DE MAQUINARIA", "El año debe tener 4 dígitos", "warning"); return false; }
    return true;
  };

  // idrelacion -99 = alta nueva; > 0 = edición del registro existente
  const handleGuardar = () => {
    if (!validar()) return;

    const body = {
      idcliente: Number(idcliente),
      idrelacion: maquina ? Number(maquina.idrelacion) : -99,
      categoria: form.categoria,
      tipo: form.tipo.trim(),
      marca: form.marca.trim(),
      modelo: form.modelo.toUpperCase(),
      serie: form.serie.toUpperCase(),
      anio: Number(form.anio),
      comentarios: form.comentarios,
    };

    request
      .post("ParqueMaquinariaCRM/Agregar", body)
      .then(async () => {
        await Swal.fire("PARQUE DE MAQUINARIA", "Equipo guardado con éxito", "success");
        onGuardado?.();
        onClose();
      })
      .catch((error) => Swal.fire("PARQUE DE MAQUINARIA", JSON.stringify(error), "error"));
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className="crm-modal-card paq-maq-modal-card">
        <button type="button" className="crm-modal-cerrar" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </button>

        <h3 className="crm-modal-titulo">{maquina ? "Editar equipo" : "Agregar equipo"}</h3>

        <div className="crm-modal-body">
          <div className="crm-detalle-grid crm-detalle-grid-2">
            <div className="crm-field">
              <label>Categoría</label>
              <select className="crm-field-editable" value={form.categoria} onChange={handleChange("categoria")}>
                <option value="">Seleccionar</option>
                {CategoriaMaquinariaArray.map((c) => (
                  <option key={c.id} value={c.id}>{c.display}</option>
                ))}
              </select>
            </div>
            <div className="crm-field">
              <label>Tipo</label>
              <div className="crm-home-search-section" ref={buscadorTipoRef}>
                <div className={`crm-home-search-bar${resultadosTipo.length > 0 ? " search-open" : ""}`}>
                  <SearchIcon className="crm-search-icon" />
                  <input
                    type="text"
                    className="crm-search-input"
                    placeholder="Escribir o elegir tipo..."
                    value={form.tipo}
                    onChange={handleChange("tipo")}
                    onFocus={() => setMostrarTipos(true)}
                    autoComplete="off"
                  />
                </div>

                {resultadosTipo.length > 0 && posicionTipo && createPortal(
                  <div
                    ref={dropdownTipoRef}
                    className="crm-search-dropdown crm-search-dropdown-fixed"
                    style={{ top: posicionTipo.top, left: posicionTipo.left, width: posicionTipo.width }}
                  >
                    {resultadosTipo.map((t) => (
                      <div key={t.id} className="crm-search-result" onClick={() => handleSeleccionarTipo(t)}>
                        <div className="crm-res-info">
                          <span className="crm-res-nombre">{t.display}</span>
                        </div>
                      </div>
                    ))}
                  </div>,
                  document.body
                )}
              </div>
            </div>
          </div>

          <div className="crm-detalle-grid crm-detalle-grid-2">
            <div className="crm-field">
              <label>Marca</label>
              {marcaBloqueada ? (
                <div className="crm-detalle-cliente-asociado">
                  <div>
                    <span className="asociado-nombre">{marcaSeleccionada?.display || MARCA_JOHN_DEERE}</span>
                  </div>
                </div>
              ) : (
                <div className="crm-home-search-section" ref={buscadorMarcaRef}>
                  <div className={`crm-home-search-bar${resultadosMarca.length > 0 ? " search-open" : ""}`}>
                    <SearchIcon className="crm-search-icon" />
                    <input
                      type="text"
                      className="crm-search-input"
                      placeholder="Escribir o elegir marca..."
                      value={form.marca}
                      onChange={handleChange("marca")}
                      onFocus={() => setMostrarMarcas(true)}
                      autoComplete="off"
                    />
                  </div>

                  {resultadosMarca.length > 0 && posicionMarca && createPortal(
                    <div
                      ref={dropdownMarcaRef}
                      className="crm-search-dropdown crm-search-dropdown-fixed"
                      style={{ top: posicionMarca.top, left: posicionMarca.left, width: posicionMarca.width }}
                    >
                      {resultadosMarca.map((m) => (
                        <div key={m.id} className="crm-search-result" onClick={() => handleSeleccionarMarca(m)}>
                          <div className="crm-res-info">
                            <span className="crm-res-nombre">{m.display}</span>
                          </div>
                        </div>
                      ))}
                    </div>,
                    document.body
                  )}
                </div>
              )}
            </div>
            <div className="crm-field">
              <label>Modelo</label>
              <input type="text" className="crm-field-editable" value={form.modelo} onChange={handleChange("modelo")} maxLength={50} />
            </div>
          </div>

          <div className="crm-detalle-grid crm-detalle-grid-2">
            <div className="crm-field">
              <label>Serie</label>
              <input type="text" className="mono crm-field-editable" value={form.serie} onChange={handleChange("serie")} maxLength={50} />
            </div>
            <div className="crm-field">
              <label>Año</label>
              <input type="text" className="mono crm-field-editable" value={form.anio} onChange={handleChangeAnio} maxLength={4} />
            </div>
          </div>

          <div className="crm-detalle-grid crm-detalle-grid-1">
            <div className="crm-field">
              <label>Comentarios</label>
              <textarea className="crm-field-editable crm-modal-textarea" rows={3} value={form.comentarios} onChange={handleChange("comentarios")} maxLength={250} />
            </div>
          </div>
        </div>

        <div className="crm-modal-buttons">
          <button type="button" className="btn-secundario-outline" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn-primario-solido" onClick={handleGuardar}>Guardar</button>
        </div>
      </div>
    </Modal>
  );
};
